import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Icon,
  Modal,
  Header,
  List,
  Message,
  Loader,
  Dimmer,
  Segment,
} from "semantic-ui-react";
import { sendErrorNotification } from "@components/Notifications";

export class WeedoutButton extends Component {
  render() {
    const {
      fluid = false,
      content = "Weed-out",
      labelPosition = "left",
      icon = "minus circle",
      color = "orange",
      ...props
    } = this.props;

    return (
      <Button
        fluid={fluid}
        content={content}
        labelPosition={labelPosition}
        icon={icon}
        color={color}
        {...props}
      />
    );
  }
}

WeedoutButton.propTypes = {
  fluid: PropTypes.bool,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  labelPosition: PropTypes.string,
  icon: PropTypes.string,
  color: PropTypes.string,
};

export class WeedoutRecordModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isModalOpen: false,
      isLoading: false,
      references: {},
      hasReferences: false,
      checkingReferences: false,
    };
  }

  toggle = () => {
    this.setState({
      isModalOpen: !this.state.isModalOpen,
      references: {},
      hasReferences: false,
    });
  };

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isModalOpen && this.state.isModalOpen) {
      this.checkReferences();
    }
  }

  checkReferences = async () => {
    const { refProps } = this.props;

    this.setState({ checkingReferences: true });

    const references = {};
    let hasReferences = false;

    try {
      for (const refProp of refProps) {
        const refData = await refProp.getRefData();

        if (refData.data.hits.length > 0) {
          const activeRefs = refData.data.hits.filter(
            (ref) =>
              ref.status &&
              ["PENDING", "ACCEPTED", "ACTIVE", "IN_PROGRESS"].includes(
                ref.status
              )
          );

          if (activeRefs.length > 0) {
            references[refProp.refType] = activeRefs;
            hasReferences = true;
          }
        }
      }

      this.setState({
        references,
        hasReferences,
        checkingReferences: false,
      });
    } catch (error) {
      this.setState({
        checkingReferences: false,
        hasReferences: true,
      });
      sendErrorNotification(
        "Reference Check Failed",
        "Failed to check document references. Please try again."
      );
    }
  };

  handleWeedout = async () => {
    const { onWeedout } = this.props;

    this.setState({ isLoading: true });

    try {
      await onWeedout();
      this.toggle();
    } catch (error) {
      // Error handling can be added here if needed
    } finally {
      this.setState({ isLoading: false });
    }
  };

  renderReferences = () => {
    const { references } = this.state;
    const { refProps } = this.props;

    return Object.entries(references).map(([refType, refs]) => {
      const refProp = refProps.find((prop) => prop.refType === refType);

      return (
        <div key={refType} style={{ marginBottom: "1rem" }}>
          <Header as="h5">
            You cannot weed-out the record, the following {refType} records use
            it!
          </Header>
          <List ordered>
            {refs.map((ref, index) => (
              <List.Item
                key={index}
                style={{
                  cursor: refProp.onRefClick ? "pointer" : "default",
                  color: refProp.onRefClick ? "#4183c4" : "inherit",
                }}
                onClick={() =>
                  refProp.onRefClick &&
                  refProp.onRefClick(ref.record?.pid || ref.pid)
                }
              >
                <Icon name="external" />
                {ref.id ||
                  `${refType} - ${ref.record?.pid || ref.pid || "Unknown"}`}
              </List.Item>
            ))}
          </List>
        </div>
      );
    });
  };

  render() {
    const {
      weedoutHeader,
      weedoutText,
      trigger: Trigger,
      weedoutButtonText = "Weed-out",
      weedoutButtonIcon = "minus circle",
      weedoutButtonColor = "orange",
    } = this.props;

    const { isModalOpen, isLoading, hasReferences, checkingReferences } =
      this.state;

    return (
      <Modal
        trigger={<Trigger onClick={this.toggle} />}
        open={isModalOpen}
        onClose={this.toggle}
        size="small"
      >
        <Modal.Header>
          {hasReferences && !checkingReferences
            ? "Cannot Weed-out Document"
            : "Weed-out Document"}
        </Modal.Header>

        <Modal.Content>
          {checkingReferences ? (
            <Dimmer active>
              <Loader>Checking for references...</Loader>
            </Dimmer>
          ) : (
            <>
              <Header as="h4">{weedoutHeader}</Header>

              {weedoutText && <div>{weedoutText}</div>}

              {hasReferences ? (
                <Message>
                  <p>
                    This document cannot be weeded out because it has active
                    references that must be resolved first:
                  </p>
                  <Segment basic>{this.renderReferences()}</Segment>
                </Message>
              ) : (
                <Message>
                  <Message.Header>Ready for Weed-out</Message.Header>
                  <p>
                    No active references found. This document can be safely
                    weeded out.
                  </p>
                </Message>
              )}
            </>
          )}
        </Modal.Content>

        <Modal.Actions>
          <Button
            onClick={this.toggle}
            disabled={isLoading || checkingReferences}
          >
            <Icon name="cancel" />
            Cancel
          </Button>

          {!hasReferences && !checkingReferences && (
            <Button
              color={weedoutButtonColor}
              onClick={this.handleWeedout}
              loading={isLoading}
              disabled={isLoading}
            >
              <Icon name={weedoutButtonIcon} />
              {weedoutButtonText}
            </Button>
          )}
        </Modal.Actions>
      </Modal>
    );
  }
}

WeedoutRecordModal.propTypes = {
  weedoutHeader: PropTypes.string.isRequired,
  weedoutText: PropTypes.node,
  refProps: PropTypes.arrayOf(
    PropTypes.shape({
      refType: PropTypes.string.isRequired,
      onRefClick: PropTypes.func.isRequired,
      getRefData: PropTypes.func.isRequired,
    })
  ).isRequired,
  onWeedout: PropTypes.func.isRequired,
  trigger: PropTypes.elementType.isRequired,
  weedoutButtonText: PropTypes.string,
  weedoutButtonIcon: PropTypes.string,
  weedoutButtonColor: PropTypes.string,
};

WeedoutRecordModal.defaultProps = {
  weedoutText: null,
  weedoutButtonText: "Weed-out",
  weedoutButtonIcon: "minus circle",
  weedoutButtonColor: "orange",
};
